import { AlertCircle, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import React, { ReactNode } from 'react';
import reactHotToast, {
  DefaultToastOptions,
  Toaster as ReactHotToaster,
  Renderable,
  ValueOrFunction
} from 'react-hot-toast';
import { VariantProps } from 'tailwind-variants';

import { Button, buttonVariants } from '../Button';

type ToasterProps = React.ComponentProps<typeof ReactHotToaster>;

type PromiseMessages<T> = {
  loading: Renderable;
  success?: ValueOrFunction<Renderable, T>;
  error?: ValueOrFunction<Renderable, unknown>;
};

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <ReactHotToaster
      position="top-right"
      gutter={12}
      containerStyle={{ top: 76, right: 20 }}
      toastOptions={{
        duration: 4000,
        style: {
          maxWidth: '380px',
          background: '#fff',
          color: 'var(--color-foreground)',
          border: '1px solid var(--color-border)',
          borderRadius: '0.625rem',
          boxShadow: '0 12px 32px -12px oklch(20.05% 0.0191 248.8 / 0.28)',
          padding: '12px 14px',
          fontSize: '0.875rem'
        }
      }}
      {...props}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function toast(toastProps: ToastProps) {
  const { title, description, button, color, icon, duration } = toastProps;

  // Structured content — bold title + muted, wrapping description. Width is capped on
  // the Toaster so long messages (e.g. a long role list) wrap instead of blowing out.
  const message = (
    <div className="flex min-w-0 flex-col gap-0.5">
      {title ? <p className="text-foreground text-sm font-semibold">{title}</p> : null}
      {description ? (
        <p className="text-muted-foreground text-sm break-words whitespace-pre-line">
          {description}
        </p>
      ) : null}
    </div>
  );

  const toastOptions: Parameters<typeof reactHotToast>[1] = {
    duration: duration || 4000,
    ...(icon && { icon: icon as string | React.ReactElement })
  };

  // If there's a button, use custom toast to render it
  if (button) {
    return reactHotToast.custom(
      (t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            {icon && <span style={{ marginRight: '8px' }}>{icon}</span>}
            <div>
              {title && <div style={{ fontWeight: 600 }}>{title}</div>}
              {description && (
                <div style={{ fontSize: '14px', opacity: 0.8 }}>{description}</div>
              )}
            </div>
          </div>
          <Button
            color={button.color}
            variant="outlined"
            size="sm"
            onClick={() => {
              button.onClick();
              reactHotToast.dismiss(t.id);
            }}
          >
            {button.label}
          </Button>
        </div>
      ),
      {
        duration: duration || 4000
      }
    );
  }

  // Use appropriate toast method based on color when no button
  switch (color) {
    case 'success':
      return reactHotToast.success(message, {
        ...toastOptions,
        icon: (icon as string | React.ReactElement) || (
          <CheckCircle className="fill-success size-6.5 shrink-0 text-white" />
        )
      });
    case 'danger':
      return reactHotToast.error(message, {
        ...toastOptions,
        icon: (icon as string | React.ReactElement) || (
          <AlertCircle className="fill-danger size-6.5 shrink-0 text-white" />
        )
      });
    case 'warning':
      return reactHotToast(message, {
        ...toastOptions,
        icon: (icon as string | React.ReactElement) || (
          <AlertTriangle className="fill-warning size-6.5 shrink-0 text-white" />
        )
      });
    case 'info':
      return reactHotToast(message, {
        ...toastOptions,
        icon: (icon as string | React.ReactElement) || (
          <AlertCircle className="fill-info size-6.5 shrink-0 text-white" />
        )
      });
    case 'primary':
    case 'secondary':
    case 'muted':
    default:
      return reactHotToast(message, toastOptions);
  }
}

type ToastProps = {
  title?: string;
  description?: string;
  icon?: ReactNode;
  button?: {
    label: string;
    onClick: () => void;
    color?: VariantProps<typeof buttonVariants>['color'];
  };
  duration?: number;
  color?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'muted' | 'info';
};

toast.promise = function <T>(
  promise: Promise<T>,
  messages: PromiseMessages<T>,
  options?: DefaultToastOptions
): Promise<T> {
  return reactHotToast.promise(
    promise,
    {
      loading: messages.loading,
      success: messages.success,
      error: messages.error
    },
    {
      loading: {
        icon: <Loader2 className="text-primary size-6.5 shrink-0 animate-spin" />,
        duration: Infinity,
        ...options?.loading
      },
      success: {
        icon: <CheckCircle className="fill-success size-6.5 shrink-0 text-white" />,
        duration: 4000,
        ...options?.success
      },
      error: {
        icon: <AlertCircle className="fill-danger size-6.5 shrink-0 text-white" />,
        duration: 4000,
        ...options?.error
      },
      ...options
    }
  );
};

export { Toaster };
